import React, { useEffect, useMemo, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './MainDashboardMap.css'

const DEFAULT_DEVICE_POSITION = [15.133109070344, 120.5899466269832]

const ML_STATUS_MARKER_COLORS = {
  0: '#9ca3af',
  1: '#00c853',
  2: '#f97316',
  3: '#e52222',
}

const fallbackMetricCards = [
  {
    label: 'Power Import',
    value: 'N/A',
    detail: 'Current import',
  },
  {
    label: 'Power Export',
    value: 'N/A',
    detail: 'Current export',
  },
  {
    label: 'GreenScore',
    value: 'N/A',
    detail: 'Sustainability index',
  },
  {
    label: 'Estimated Savings',
    value: 'N/A',
    detail: 'Based on PHP 11.743 per kWh',
  },
]

function createSiteIcon(color) {
  return L.divIcon({
    className: 'dashboard-leaflet-marker',
    html: `<span style="--marker-color: ${color}"></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -12],
  })
}

function unwrapBoardInfo(boardInfo) {
  if (!boardInfo || typeof boardInfo !== 'object') {
    return null
  }

  if (boardInfo.success !== undefined && boardInfo.data && typeof boardInfo.data === 'object') {
    return boardInfo.data
  }

  return boardInfo
}

function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()

    if (!trimmedValue) {
      return null
    }

    const parsed = Number(trimmedValue)

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function hasValidCoordinates(latitude, longitude) {
  if (latitude === null || longitude === null) {
    return false
  }

  if (latitude === 0 && longitude === 0) {
    return false
  }

  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
}

function formatKw(value, fallbackValue) {
  const number = toNumber(value)

  return number === null ? fallbackValue : `${number.toFixed(2)} kW`
}

function formatScore(value, fallbackValue) {
  const number = toNumber(value)

  return number === null ? fallbackValue : number.toFixed(1)
}

function formatPhp(value, fallbackValue) {
  const number = toNumber(value)

  return number === null ? fallbackValue : `PHP ${number.toFixed(2)}`
}

function getPowerImport(payload) {
  const data = payload?.data || {}

  return toNumber(data.powerImport ?? payload?.powerImport ?? payload?.devicedata?.meter?.import?.W)
}

function getPowerExport(payload) {
  const data = payload?.data || {}

  return toNumber(data.powerExport ?? payload?.powerExport ?? payload?.devicedata?.meter?.export?.W)
}

function getGreenScore(payload) {
  const data = payload?.data || {}

  return toNumber(data.greenScore ?? payload?.greenScore ?? payload?.ml?.greenScore)
}

function getEstimatedSavings(payload) {
  const data = payload?.data || {}

  return toNumber(
    data.estimatedSavings
      ?? data.estimatedSavingsPhp
      ?? data.savings
      ?? data.savingsPhp
      ?? payload?.estimatedSavings
      ?? payload?.estimatedSavingsPhp
      ?? payload?.savings
      ?? payload?.savingsPhp
  )
}

function sumValues(values) {
  const numbers = values.filter((value) => value !== null)

  return numbers.length ? numbers.reduce((sum, value) => sum + value, 0) : null
}

function averageValues(values) {
  const numbers = values.filter((value) => value !== null)

  return numbers.length ? numbers.reduce((sum, value) => sum + value, 0) / numbers.length : null
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}

function getDeviceDashboardUrl(deviceId) {
  return `/dashboard/${encodeURIComponent(deviceId)}`
}

function getMlStatusCode(payload) {
  const statusCode = payload?.ml?.status

  if (statusCode === undefined || statusCode === null || statusCode === '') {
    return null
  }

  return String(statusCode).trim()
}

function getMarkerColor(payload) {
  const statusCode = getMlStatusCode(payload)

  if (statusCode && ML_STATUS_MARKER_COLORS[statusCode]) {
    return ML_STATUS_MARKER_COLORS[statusCode]
  }

  const fault = payload?.ml?.fault

  if (fault !== undefined && String(fault) !== '0') {
    return '#e52222'
  }

  return ML_STATUS_MARKER_COLORS[1]
}

function getBoardMetricCards(boardInfos = []) {
  const payloads = boardInfos.map(unwrapBoardInfo).filter(Boolean)

  if (!payloads.length) {
    return fallbackMetricCards
  }

  const totalPowerImport = sumValues(payloads.map(getPowerImport))
  const totalPowerExport = sumValues(payloads.map(getPowerExport))
  const averageGreenScore = averageValues(payloads.map(getGreenScore))
  const totalEstimatedSavings = sumValues(payloads.map(getEstimatedSavings))

  return fallbackMetricCards.map((metric) => {
    if (metric.label === 'Power Import') {
      return {
        ...metric,
        value: formatKw(totalPowerImport, metric.value),
        detail: 'Total import',
      }
    }

    if (metric.label === 'Power Export') {
      return {
        ...metric,
        value: formatKw(totalPowerExport, metric.value),
        detail: 'Total export',
      }
    }

    if (metric.label === 'GreenScore') {
      return {
        ...metric,
        value: formatScore(averageGreenScore, metric.value),
        detail: 'Average index',
      }
    }

    if (metric.label === 'Estimated Savings') {
      return {
        ...metric,
        value: formatPhp(totalEstimatedSavings, metric.value),
      }
    }

    return metric
  })
}

function getBoardSiteMarkersFromList(boardInfos = []) {
  return boardInfos
    .map((boardInfo) => {
      const payload = unwrapBoardInfo(boardInfo)

      if (!payload) {
        return null
      }

      const device = payload?.config?.Device
      const data = payload?.data || {}
      const latitude = toNumber(device?.Lat ?? device?.lat ?? device?.Latitude ?? device?.latitude ?? data.lat ?? data.latitude)
      const longitude = toNumber(device?.Lon ?? device?.lon ?? device?.Lng ?? device?.lng ?? device?.Longitude ?? device?.longitude ?? data.lon ?? data.lng ?? data.longitude)
      const hasCoordinates = hasValidCoordinates(latitude, longitude)
      const statusCode = getMlStatusCode(payload)
      const status = statusCode ? `Status ${statusCode}` : 'Online'

      return {
        name: device?.ID || payload?.data?.DeviceID || payload?.data?.deviceId || payload?.id || 'Device',
        status: hasCoordinates ? status : `${status} - Default location`,
        position: hasCoordinates ? [latitude, longitude] : DEFAULT_DEVICE_POSITION,
        color: getMarkerColor(payload),
      }
    })
    .filter(Boolean)
}

function MainDashboardMap({ boardInfo = null, boardInfos = [] }) {
  const displayedBoardInfos = useMemo(() => {
    return boardInfos.length ? boardInfos : [boardInfo]
  }, [boardInfo, boardInfos])
  const metricCards = useMemo(() => getBoardMetricCards(displayedBoardInfos), [displayedBoardInfos])
  const siteMarkers = useMemo(() => {
    return getBoardSiteMarkersFromList(displayedBoardInfos)
  }, [displayedBoardInfos])
  const mapElementRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markerLayerRef = useRef(null)

  useEffect(() => {
    if (!mapElementRef.current || mapInstanceRef.current) {
      return undefined
    }

    const map = L.map(mapElementRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView(DEFAULT_DEVICE_POSITION, 10)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map)

    mapInstanceRef.current = map
    setTimeout(() => map.invalidateSize(), 0)

    return () => {
      if (markerLayerRef.current) {
        markerLayerRef.current.remove()
        markerLayerRef.current = null
      }

      map.remove()
      mapInstanceRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapInstanceRef.current

    if (!map) {
      return undefined
    }

    if (markerLayerRef.current) {
      markerLayerRef.current.remove()
    }

    const markerLayer = L.layerGroup()

    siteMarkers.forEach((marker) => {
      const escapedName = escapeHtml(marker.name)
      const dashboardUrl = getDeviceDashboardUrl(marker.name)

      L.marker(marker.position, { icon: createSiteIcon(marker.color) })
        .addTo(markerLayer)
        .bindPopup(
          `<a class="dashboard-map__popup-link" href="${dashboardUrl}">${escapedName}</a><br />${escapeHtml(marker.status)}`
        )
    })

    markerLayer.addTo(map)
    markerLayerRef.current = markerLayer

    if (siteMarkers.length === 1) {
      map.setView(siteMarkers[0].position, 13)
    } else if (siteMarkers.length > 1) {
      const bounds = L.latLngBounds(siteMarkers.map((marker) => marker.position))
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: 12 })
    }

    return () => {
      markerLayer.remove()

      if (markerLayerRef.current === markerLayer) {
        markerLayerRef.current = null
      }
    }
  }, [siteMarkers])

  return (
    <section className="dashboard-map-card" aria-label="Site map and energy metrics">
      <div className="dashboard-map">
        <div className="dashboard-map__canvas" ref={mapElementRef} aria-label="OpenStreetMap site view"></div>

        <div className="dashboard-metrics">
          {metricCards.map((metric) => (
            <article className="dashboard-metric-card" key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <p>{metric.detail}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export default MainDashboardMap
